import { Group, Stack, Table, ThemeIcon, useMantineTheme } from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { useState } from 'react';
import { useExtendedUserList } from '../../../../api/hooks/user/useExtendedUserList';
import { sanitizePromptMessage } from '../../../../components/AIAssistant/utils';
import { UserAvatar } from '../../../../components/UserAvatar';
import { AiChatPreviewModalController } from '../../../../features/AiChatPreviewModal/AiChatPreviewModalController';
import { AiQualityBadgeController } from '../../../../features/AiQualityBadge';
import { formatShortTimeDifference } from '../../../../utils/time';
import { MetricAIQueryTableAll } from './MetricAIQueryTableAll';
import type { TableComponentProps } from './MetricWidgetTable';

function MetricAIQueryTable({
	values,
	withUser,
	loadAll,
}: TableComponentProps & { withUser: boolean; loadAll?: boolean }) {
	const [promptId, setPromptId] = useState<string | null>(null);
	const theme = useMantineTheme();
	const { users } = useExtendedUserList();
	const findUserById = (id: string) => users.find((u) => u.id === id);

	if (loadAll) {
		return <MetricAIQueryTableAll />;
	}

	return (
		<>
			{promptId && (
				<AiChatPreviewModalController
					promptId={promptId}
					onClose={() => setPromptId(null)}
				/>
			)}
			<Table highlightOnHover>
				<thead>
					<tr>
						<th>
							<Text color="text/secondary/default" fz="xs" fw="semibold">
								Initial question
							</Text>
						</th>
						<th style={{ width: '80px' }}>
							<Text color="text/secondary/default" fz="xs" fw="semibold">
								Score
							</Text>
						</th>
					</tr>
				</thead>
				<tbody>
					{values.map((prompt) => {
						const user = findUserById(String(prompt.user));
						return (
							<tr
								style={{
									cursor: 'pointer',
								}}
								key={prompt.id}
								onClick={() => {
									setPromptId(String(prompt.id));
								}}
							>
								<td>
									<Group h={52} spacing="sm" noWrap>
										{user && withUser ? (
											<UserAvatar user={user} size={28} />
										) : (
											<ThemeIcon
												color={theme.other.getColor('fill/secondary/default')}
												size={28}
												radius={28}
											>
												<Icon name="sparkles" color="icon/primary/default" />
											</ThemeIcon>
										)}
										<Stack spacing={0}>
											<Text
												lineClamp={1}
												color="text/primary/default"
												size="sm"
												weight="semibold"
											>
												{sanitizePromptMessage(String(prompt.prompt))}
											</Text>
											<Text color="text/secondary/default" size="xs">
												{user &&
													withUser &&
													`Asked by ${user?.display_name} · `}{' '}
												{formatShortTimeDifference(String(prompt.created_at))}
											</Text>
										</Stack>
									</Group>
								</td>
								<td>
									<AiQualityBadgeController score={Number(prompt.score ?? 0)} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>
		</>
	);
}

export function MetricAIQueryTableWithUsers({
	values,
	loadAll,
}: TableComponentProps) {
	return (
		<MetricAIQueryTable
			values={values}
			infoColumns={[]}
			withUser
			loadAll={loadAll}
		/>
	);
}

export function MetricAIQueryTableWithoutUsers({
	values,
}: TableComponentProps) {
	return (
		<MetricAIQueryTable values={values} infoColumns={[]} withUser={false} />
	);
}
